import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { columns } from "./_columns";
import { summary } from "./_footer";

import { 
    Button,
    FloatButton,
    Table,
    Typography
} from "antd";

import { ReactComponent as RightArrow } from "_assets/right_arrow.svg";

import styles from "_styles/dashboard/LicensesListing.module.css";
import { isEmpty, isNonEmptyArray } from "_helpers";
 
export function LicensesListing({ fetchData, selectedBase }){
    const { Title } = Typography;
    const { BackTop } = FloatButton;

    const initialDisableRowsAreVisible = useRef({
        main: false,
        ext: false,
        other: false
    }).current;
    const [ disableRowsAreVisible, setDisableRowsAreVisible ] = useState(initialDisableRowsAreVisible);

    useEffect(() => {
        setDisableRowsAreVisible(initialDisableRowsAreVisible);
    }, [selectedBase])

    const handleOnRow = useCallback(( ) => {
        return {
            onCopy: (event) => {
                const selection = document.getSelection();

                event.clipboardData.setData("text/plain", selection.toString().trim());
                event.preventDefault();
            }
        }
    }, []);

    const isEmptyDate = d => (!d || d === '0000-00-00' || d === '0000-00-00 00:00:00');

    const data = useMemo(() => {
        const rawData = fetchData?.[selectedBase];
        let newData = {
            main: [],
            ext: [],
            other: [],
        };
        if( !rawData || isEmpty(rawData) ) return newData;

        Object.keys(newData).forEach(tk => {
            const currData = (rawData?.[tk] || []).map(el => el);
            currData.forEach(e => {
                if( disableRowsAreVisible[tk] === false && e.is_disabled === true ) return ;
                newData[tk].push(e);
            })
            newData[tk].sort((a,b) => {
                // 1️. D’abord : actifs avant désactivés
                if( a.is_disabled !== b.is_disabled ) {
                    return a.is_disabled ? 1 : -1;
                }

                // 2️. Si les deux sont actifs
                if( !a.is_disabled && !b.is_disabled ) {
                    const aEmpty = isEmptyDate(a.date_activation);
                    const bEmpty = isEmptyDate(b.date_activation);

                    // Si l’un a une date et pas l’autre
                    if( aEmpty && !bEmpty ) return 1;
                    if( !aEmpty && bEmpty ) return -1;
                    // Trier par date_activation (croissante)
                    if( !aEmpty && !bEmpty ){ 
                        return new Date(a.date_activation) - new Date(b.date_activation);
                    }
                    return 0;
                }

                // 3️. Si les deux sont désactivés
                if( a.is_disabled && b.is_disabled ) {
                    const aEmpty = isEmptyDate(a.date_deactivation);
                    const bEmpty = isEmptyDate(b.date_deactivation);

                    if( aEmpty && !bEmpty ) return 1;
                    if( !aEmpty && bEmpty ) return -1;
                    // Trier par date_deactivation (croissante)
                    if( !aEmpty && !bEmpty ){
                        return new Date(a.date_deactivation) - new Date(b.date_deactivation);
                    }
                    return 0;
                }

                return 0;
            })
        })

        return newData;
    }, [fetchData, selectedBase, disableRowsAreVisible]);

    const hasDisabledRows = useMemo(() => {
        const rawData = fetchData?.[selectedBase];
        let disabledRows = {
            main: false,
            ext: false,
            other: false
        };
        if( !rawData || isEmpty(rawData) ) return disabledRows;

        Object.keys(disabledRows).forEach(tk => {
            const currData = (rawData?.[tk] || []).map(el => el);
            disabledRows[tk] = currData.some(el => el.is_disabled === true);
        })

        return disabledRows;
    }, [fetchData, selectedBase]);

    const defaults = useMemo(() => ({
        columns,
        bordered: true,
        pagination: false,
        showSorterTooltip: false,
        rowClassName: (rec) => rec.is_disabled ? 'row-inactive editable-row' : 'editable-row',
        onRow: handleOnRow // Hack 22/03/23 Bug present in antd table, when copying value, many blanks charaters are adding
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [])

    const handleDisableRowsDisplay = ( tableKey ) => {
        setDisableRowsAreVisible(prevVal => ({
            ...prevVal,
            [tableKey]: !prevVal[tableKey]
        }))
    }

    return(
        <div>
            {
                isNonEmptyArray(fetchData?.[selectedBase]?.['main'] || []) &&
                    <div>
                        <div className="d-flex align-items-center my-3">
                            <Title level={2} className="m-0">Licences principales</Title>
                            {
                                hasDisabledRows.main
                                &&
                                <Button type={disableRowsAreVisible.main ? "primary" : undefined} className="ms-auto" onClick={() => handleDisableRowsDisplay('main')}>
                                    Afficher désactivées
                                </Button>
                            }
                        </div>
                        <Table
                            dataSource={data.main}
                            summary={() => summary(fetchData?.[selectedBase]?.['main'], disableRowsAreVisible.main, columns)}
                            {...defaults}
                        />
                    </div>
            }
            {
                isNonEmptyArray(fetchData?.[selectedBase]?.['ext'] || []) &&
                    <div>
                        <div className="d-flex align-items-center my-3">
                            <Title level={2} className="m-0">Licences ext</Title>
                            {
                                hasDisabledRows.ext
                                &&
                                <Button type={disableRowsAreVisible.ext ? "primary" : undefined} className="ms-auto" onClick={() => handleDisableRowsDisplay('ext')}>
                                    Afficher désactivées
                                </Button>
                            }
                        </div>
                        <Table
                            dataSource={data.ext}
                            summary={() => summary(fetchData?.[selectedBase]?.['ext'], disableRowsAreVisible.ext, columns)}
                            {...defaults}
                        />
                    </div>
            }
            {
                isNonEmptyArray(fetchData?.[selectedBase]?.['other'] || []) &&
                    <div>
                        <div className="d-flex align-items-center my-3">
                            <Title level={2} className="m-0">Autres licences</Title>
                            {
                                hasDisabledRows.other
                                &&
                                <Button type={disableRowsAreVisible.other ? "primary" : undefined} className="ms-auto" onClick={() => handleDisableRowsDisplay('other')}>
                                    Afficher désactivées
                                </Button>
                            }
                        </div>
                        <Table
                            dataSource={data.other}
                            summary={() => summary(fetchData?.[selectedBase]?.['other'], disableRowsAreVisible.other, columns)}
                            {...defaults}
                        />
                    </div>
            }
            <BackTop 
                className={styles.backtop}
                style={{
                    right: 84,
                    bottom: 56,
                }}
                duration={200}
                icon={
                    <RightArrow 
                        height={20} 
                        width={20} 
                        transform="rotate(-90)" 
                        viewBox="0 0 30 50"
                    />
                }
            />
        </div>
    );
}